import { useCallback, useEffect } from 'react';

import { type Event, Fit, Layout, type RiveEventPayload } from '@rive-app/react-canvas';
import { createPortal } from 'react-dom';

import xpGiftRive from '../../../assets/missions/xp_gift.riv';
import { useRiveMachine } from '../../../hooks/rive/useRiveMachine';
import { useFlag } from '../../../services/launchDarkly';
import type { MissionsXpRiveConfig } from '../../../services/types';
import { fireTrigger, setNumber } from '../../../utils/riveHelper';
import { DEFAULT_MISSIONS_XP_RIVE_CONFIG } from '../constants';

import classes from './MissionXpGiftAnimation.module.scss';

const STATE_MACHINE_NAME = 'State Machine 1';

interface Props {
  boxNum: number;
  onBoxOpen?: () => void;
  onBoxFinish?: () => void;
}

export function MissionXpGiftAnimation({ boxNum, onBoxOpen, onBoxFinish }: Props) {
  const handleName = useCallback(
    (raw?: string) => {
      const name = raw?.toLowerCase().replace(/[\s_]/g, '');
      if (name === 'boxopen') onBoxOpen?.();
      if (name === 'boxfinish') onBoxFinish?.();
    },
    [onBoxOpen, onBoxFinish],
  );

  const onRiveEvent = useCallback(
    (event: Event) => {
      if (import.meta.env.DEV) console.log('[xpGift] riveEvent', event?.data);
      handleName((event?.data as RiveEventPayload)?.name);
    },
    [handleName],
  );

  const onStateChange = useCallback(
    (event: Event) => {
      if (import.meta.env.DEV) console.log('[xpGift] stateChange', event?.data);
      const states = (event?.data as string[]) ?? [];
      states.forEach(handleName);
    },
    [handleName],
  );

  const riveConfig = useFlag<MissionsXpRiveConfig>('missions_xp_rive_config', DEFAULT_MISSIONS_XP_RIVE_CONFIG);

  const { rive, RiveComponent } = useRiveMachine({
    src: riveConfig.xpGiftUrl || xpGiftRive,
    stateMachineName: riveConfig.xpGiftStateMachine || STATE_MACHINE_NAME,
    layout: new Layout({ fit: Fit.Contain }),
    autoplay: true,
    autoBind: true,
    onRiveEvent,
    onStateChange,
  });

  useEffect(() => {
    const vm = rive?.viewModelInstance;
    if (!vm) return;
    setNumber(vm, 'boxNum', boxNum);
    fireTrigger(vm, 'claimGift');
  }, [rive, boxNum]);

  // Some authoring exposes boxOpen/boxFinish as Rive Events (handled above via
  // onRiveEvent/onStateChange), others as ViewModel triggers — wire both defensively.
  useEffect(() => {
    const vm = rive?.viewModelInstance;
    if (!vm) return;

    const boxOpenTrigger = vm.trigger?.('boxOpen');
    const boxFinishTrigger = vm.trigger?.('boxFinish');
    const handleBoxOpen = () => onBoxOpen?.();
    const handleBoxFinish = () => onBoxFinish?.();

    boxOpenTrigger?.on(handleBoxOpen);
    boxFinishTrigger?.on(handleBoxFinish);

    return () => {
      boxOpenTrigger?.off(handleBoxOpen);
      boxFinishTrigger?.off(handleBoxFinish);
    };
  }, [rive, onBoxOpen, onBoxFinish]);

  return createPortal(
    <div className={classes.animation}>
      <RiveComponent />
    </div>,
    document.body,
  );
}
